import React, { forwardRef, useEffect, useReducer } from 'react';
import * as PropTypes from 'prop-types';
import { get } from '../../utility/via-fetch';



const initialSvgLoadState = {
  loading: false,
  svg: undefined,
  error: undefined,
};

/**
 * A reducer for managing the load state of an SVG.
 * @param {object} state The react component state.
 * @param {object} action An action to handle.
 */
const svgLoadReducer = (state, action) => {
  switch (action.type) {
    case 'LOAD_SVG':
      return {
        ...initialSvgLoadState,
        loading: true,
      };
    case 'SUCCESS_LOAD_SVG':
      return {
        ...initialSvgLoadState,
        svg: action.payload,
      };
    case 'FAILED_LOAD_SVG':
      return {
        ...initialSvgLoadState,
        error: action.payload,
      };
    default: return { state };
  }
};

/**
 * A custom hook for loading the SVG html.
 *
 * @param {string} url The url to load the SVG from.
 * @returns {[boolean, string]} An array containing loading and svg.
 * @returns boolean loading If the hook is still loading the svg.
 * @returns string svg The svg hmtl loaded from the server.
 */
export function useViaSvg(url) {
  const [state, dispatch] = useReducer(svgLoadReducer, initialSvgLoadState);

  useEffect(() => {
    if (!url) return;

    (async () => {
      dispatch({ type: 'LOAD_SVG' });
      try {
        const html = await get(url);
        dispatch({ type: 'SUCCESS_LOAD_SVG', payload: html });
      } catch (e) {
        dispatch({ type: 'FAILED_LOAD_SVG', payload: e });
      }
    })();
  }, [url, dispatch]);

  return [state.loading, state.svg, state.error];
}


/**
 * Converts an HTML string into the actual HTML.
 *
 * WARNING:
 *  This should only be used with sanitized html.
 *  Rendering user defined html can lead to XSS attacks!
 *
 * @param {string} safeSvgHtml The HTML string to render.
 */
const ViaSvg = forwardRef((props, ref) => {
  const { safeSvgHtml } = props;

  // eslint-disable-next-line react/no-danger
  return <svg ref={ref} dangerouslySetInnerHTML={{ __html: safeSvgHtml }} />;
});

ViaSvg.propTypes = {
  safeSvgHtml: PropTypes.string,
};

ViaSvg.defaultProps = {
  safeSvgHtml: '',
};

export default ViaSvg;
